import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, CustomerListItem } from '../types';
import { userApi, customerApi } from '../services/api';
import CustomerTable from '../components/common/CustomerTable';
import INDUSTRY_TYPES from '../constants/industries';
import { toast } from 'react-toastify';
import Sidebar from '../components/layout/Sidebar';
import { HelpButton } from '../components/common/HelpButton';
import { InfoModal } from '../components/common/InfoModal';

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState<User | undefined>();
    const [allCustomers, setAllCustomers] = useState<CustomerListItem[]>([]);
    const [myCustomers, setMyCustomers] = useState<CustomerListItem[]>([]);
    const [isLoading, setIsLoading] = useState(true); // For initial page load
    const [isTableLoading, setIsTableLoading] = useState(false); // For table data refresh
    const [isLoadingRecentCustomers, setIsLoadingRecentCustomers] = useState(false); // For recent customers
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [showFilter, setShowFilter] = useState(false);
    const [industryFilter, setIndustryFilter] = useState<string>('');
    const [showMyCustomers, setShowMyCustomers] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newCustomer, setNewCustomer] = useState({
        name: '',
        industry: 'Generic Modernization',
        customIndustry: '',
        createdBy: '',
        ERPAccountNumber: ''
    });
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showErpInfo, setShowErpInfo] = useState(false);

    // Pagination state
    const [pageSize] = useState(8);
    const [currentPage, setCurrentPage] = useState(1);
    const currentUserName = currentUser?.name || '';

    // My Customers pagination state (client-side)
    const [myCustomersCurrentPage, setMyCustomersCurrentPage] = useState(1);

    // Fetch all customers for client-side search, filtering, and pagination.
    const fetchAllCustomers = useCallback(async () => {
        try {
            setIsTableLoading(true);
            const aggregatedCustomers: CustomerListItem[] = [];
            let lastEvaluatedKey: string | undefined;

            do {
                const result = await customerApi.getCustomerList(100, lastEvaluatedKey);
                aggregatedCustomers.push(...(result.items || []));
                lastEvaluatedKey = result.lastEvaluatedKey;
            } while (lastEvaluatedKey);

            setAllCustomers(aggregatedCustomers);
        } catch (error) {
            console.error('Error fetching all customers:', error);
            setAllCustomers([]);
        } finally {
            setIsTableLoading(false);
        }
    }, []);

    // Fetch my customers data for recent activity
    const fetchMyCustomers = useCallback(async () => {
        try {
            setIsLoadingRecentCustomers(true);
            const myCustomersList = await customerApi.getMyCustomerList();
            setMyCustomers(myCustomersList);
        } catch (error) {
            console.error('Error fetching my customers:', error);
            setMyCustomers([]);
        } finally {
            setIsLoadingRecentCustomers(false);
        }
    }, []);

    // Initial data fetch for the whole page
    const fetchInitialData = useCallback(async () => {
        try {
            setIsLoading(true);
            const userData = await userApi.getCurrentUser();
            setCurrentUser(userData);

            // Update the newCustomer form with the current user's name
            setNewCustomer(prev => ({
                ...prev,
                createdBy: userData?.name || ''
            }));

            // Fetch only my customers on initial load; full list is fetched when user toggles to "All Customers"
            await fetchMyCustomers();
        } catch (error) {
            console.error('Error fetching data:', error);
            setAllCustomers([]);
            setMyCustomers([]);
        } finally {
            setIsLoading(false);
        }
    }, [fetchMyCustomers]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    // Close modal with Escape key
    useEffect(() => {
        if (!showAddModal) return;
        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setShowAddModal(false)
            }
        };

        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [showAddModal]);

    // Form validation
    const validateForm = useCallback((): boolean => {
        const errors: { [key: string]: string } = {};

        if (!newCustomer.name.trim()) {
            errors.name = 'Customer name is required';
        }

        if (newCustomer.industry === 'Custom' && !newCustomer.customIndustry.trim()) {
            errors.customIndustry = 'Custom industry is required when Custom is selected';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [newCustomer.customIndustry, newCustomer.industry, newCustomer.name]);

    // Handle adding a new customer
    const handleAddCustomer = useCallback(async () => {
        if (!validateForm() || isSubmitting) return;

        try {
            setIsSubmitting(true);

            const customerData = {
                customer_details: {
                    name: newCustomer.name,
                    industry_type: newCustomer.industry === 'Custom' ? newCustomer.customIndustry : newCustomer.industry,
                    created_by: currentUserName || newCustomer.createdBy || '',
                    ERPAccountNumber: newCustomer.ERPAccountNumber.trim() || undefined
                }
            };

            const response = await customerApi.createCustomer(customerData);
            setShowAddModal(false);

            // Reset form
            setNewCustomer({
                name: '',
                industry: 'Generic Modernization',
                customIndustry: '',
                createdBy: currentUserName,
                ERPAccountNumber: ''
            });

            toast.success('Customer created successfully!');
            if (showMyCustomers) {
                fetchMyCustomers();
            } else {
                fetchAllCustomers();
            }
            navigate(`/customer-details/${response.customer_id}`, { state: { fromDashboard: false } });
        } catch (error) {
            console.error('Error adding customer:', error);
            toast.error('Failed to create customer. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }, [currentUserName, fetchAllCustomers, fetchMyCustomers, isSubmitting, navigate, newCustomer, showMyCustomers, validateForm]);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500); // 500ms delay

        return () => {
            clearTimeout(timer);
        };
    }, [searchTerm]);

    // Load all customers when switching to the All Customers view.
    useEffect(() => {
        if (!showMyCustomers) {
            setCurrentPage(1);
            fetchAllCustomers();
        }
    }, [showMyCustomers, fetchAllCustomers]);

    // Handle page change
    const handleNextPage = () => {
        if (currentPage < allCustomersTotalPages) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const handlePageSelect = (pageNum: number) => {
        if (pageNum === currentPage || pageNum < 1 || pageNum > totalPages) {
            return;
        }
        setCurrentPage(pageNum);
    };

    // Filter myCustomers based on search term and industry filter
    const filteredMyCustomers = useMemo(() => {
        return myCustomers.filter(customer => {
            const normalizedSearch = debouncedSearchTerm.toLowerCase().trim();
            const matchesSearch = !normalizedSearch ||
                customer.name.toLowerCase().includes(normalizedSearch) ||
                customer.created_by?.toLowerCase().includes(normalizedSearch) ||
                customer.industry_type?.toLowerCase().includes(normalizedSearch);

            const matchesIndustry = !industryFilter ||
                customer.industry_type === industryFilter;

            return matchesSearch && matchesIndustry;
        });
    }, [myCustomers, debouncedSearchTerm, industryFilter]);

    // Client-side pagination for My Customers
    const myCustomersTotalPages = Math.max(1, Math.ceil(filteredMyCustomers.length / pageSize));
    const paginatedMyCustomers = useMemo(() => {
        const start = (myCustomersCurrentPage - 1) * pageSize;
        return filteredMyCustomers.slice(start, start + pageSize);
    }, [filteredMyCustomers, myCustomersCurrentPage, pageSize]);

    const filteredAllCustomers = useMemo(() => {
        return allCustomers.filter(customer => {
            const normalizedSearch = debouncedSearchTerm.toLowerCase().trim();
            const matchesSearch = !normalizedSearch ||
                customer.name.toLowerCase().includes(normalizedSearch) ||
                customer.created_by?.toLowerCase().includes(normalizedSearch) ||
                customer.industry_type?.toLowerCase().includes(normalizedSearch);

            const matchesIndustry = !industryFilter ||
                customer.industry_type === industryFilter;

            return matchesSearch && matchesIndustry;
        });
    }, [allCustomers, debouncedSearchTerm, industryFilter]);

    const allCustomersTotalPages = Math.max(1, Math.ceil(filteredAllCustomers.length / pageSize));
    const paginatedAllCustomers = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredAllCustomers.slice(start, start + pageSize);
    }, [filteredAllCustomers, currentPage, pageSize]);

    const totalPages = showMyCustomers ? myCustomersTotalPages : allCustomersTotalPages;
    const allCustomersRangeStart = filteredAllCustomers.length === 0 ? 0 : ((currentPage - 1) * pageSize) + 1;
    const allCustomersRangeEnd = filteredAllCustomers.length === 0 ? 0 : Math.min(((currentPage - 1) * pageSize) + paginatedAllCustomers.length, filteredAllCustomers.length);

    const getPaginationItems = (page: number, pages: number) => {
        if (pages <= 7) {
            return Array.from({ length: pages }, (_, index) => index + 1);
        }

        if (page <= 4) {
            return [1, 2, 3, 4, 5, 'ellipsis-end', pages];
        }

        if (page >= pages - 3) {
            return [1, 'ellipsis-start', pages - 4, pages - 3, pages - 2, pages - 1, pages];
        }

        return [1, 'ellipsis-start', page - 1, page, page + 1, 'ellipsis-end', pages];
    };

    // Reset My Customers page when filters change or when switching to My Customers
    useEffect(() => {
        setMyCustomersCurrentPage(1);
    }, [debouncedSearchTerm, industryFilter, showMyCustomers]);

    // Reset All Customers page when filters change or when switching views.
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm, industryFilter, showMyCustomers]);

    return (
        <div className="relative min-h-screen bg-white overflow-hidden font-inter">
            {/* <ApplicationBackground /> */}

            {/* Foreground Content (Header & More) */}
            <div className="flex z-10">
                {/* <Header currentUser={currentUser} /> */}
                {<Sidebar
                    recentCustomers={myCustomers}
                    onNewCustomerClick={() => setShowAddModal(true)}
                    isLoadingRecentCustomers={isLoadingRecentCustomers}
                />}
                {
                    isLoading ? (
                        <div className="flex justify-center items-center h-screen w-full">
                            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0F2A4F]"></div>
                        </div>
                    ) : (
                        <div className="mx-auto px-5 sm:px-6 lg:px-8 py-8 w-full">
                            <div className='flex flex-col md:flex-row gap-5 pr-10 mr-3 w-full'>

                                { <div className="">
                                    <h1 className="text-xl font-bold text-[#09090B]">Profiles</h1>
                                    <p className="text-gray-600 font-normal">
                                        Choose a Customer Technology Profile to review and/or update. Click the "+ New Customer" button to create a new profile for a customer not listed here.
                                    </p>
                                </div>

                               /* <div className="mb-10 w-full pl-16 border-l">
                                    <h3 className="text-2xl font-bold text-[#0F2A4F] mb-6">Recent Activity</h3>
                                    <div className="flex gap-4 w-[90%]">
                                        {recentActivity.map((activity, index) => (
                                            <div
                                                key={index}
                                                className={`${activity.color} rounded-lg px-3 py-6 shadow-sm w-[15vw] flex flex-col justify-between cursor-pointer transition-transform duration-200 hover:scale-[1.02] hover:shadow-sm `}
                                                onClick={() => handleActivityCardClick(activity.id)}
                                            >
                                                <div>
                                                    <h4 className="text-xl font-bold text-[#0F2A4F] mb-2 text-center">{activity.name}</h4>
                                                </div>

                                                <div className='flex justify-center'>
                                                    <div className='mr-2'>
                                                        <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M3.5 12.5C3.5 14.28 4.02784 16.0201 5.01677 17.5001C6.00571 18.9802 7.41131 20.1337 9.05585 20.8149C10.7004 21.4961 12.51 21.6743 14.2558 21.3271C16.0016 20.9798 17.6053 20.1226 18.864 18.864C20.1226 17.6053 20.9798 16.0016 21.3271 14.2558C21.6743 12.51 21.4961 10.7004 20.8149 9.05585C20.1337 7.41131 18.9802 6.00571 17.5001 5.01677C16.0201 4.02784 14.28 3.5 12.5 3.5C9.98395 3.50947 7.56897 4.49122 5.76 6.24L3.5 8.5M3.5 8.5V3.5M3.5 8.5H8.5M12.5 7.5V12.5L16.5 14.5" stroke="#152842" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <span className="font-medium">{activity.lastUpdated}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {
                                            recentActivity?.length === 0 && (
                                                <div className=' h-[10vh] w-full flex justify-center items-center text-[#5F6062] font-medium'>
                                                    <p>No recent activity.</p>
                                                </div>
                                            )
                                        }
                                    </div>
                                </div> */}
                            </div>

                            {/* Customers Section */}
                            <div className='py-6'>

                                {/* Search and Filter Bar */}
                                <div className="mb-4 flex justify-between items-center">
                                    <div className="relative w-full max-w-md">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Type to search for customers"
                                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#0F2A4F] focus:border-[#0F2A4F]"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <div className="flex space-x-2">
                                        <div className="flex items-center mr-3">
                                            <span className="mr-2 font-medium text-[#0F2A4F]">
                                                My Customers
                                            </span>
                                            <button
                                                onClick={() => setShowMyCustomers(!showMyCustomers)}
                                                className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none ${showMyCustomers ? 'bg-[#0F2A4F]' : 'bg-gray-300'}`}
                                            >
                                                <span
                                                    className={`inline-block w-4 h-4 transform transition-transform bg-white rounded-full ${showMyCustomers ? 'translate-x-6' : 'translate-x-1'}`}
                                                />
                                            </button>
                                        </div>
                                        
                                        <button
                                            onClick={() => setShowFilter(!showFilter)}
                                            className={`flex items-center ${showFilter ? 'bg-gray-300 text-[#0F2A4F]' : 'bg-white text-[#0F2A4F]'} border border-gray-200 rounded px-4 py-2 text-sm font-medium hover:bg-gray-200 hover:text-[#0F2A4F]`}
                                        >
                                            <svg width="18" height="18" className='mr-1' viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12.8332 2.25H1.1665L5.83317 7.76833V11.5833L8.1665 12.75V7.76833L12.8332 2.25Z" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            Filters
                                        </button>
                                    </div>
                                </div>

                                {/* Filter Panel */}
                                {showFilter && (
                                    <div className="mb-6 bg-transparent p-4 rounded shadow border border-gray-200 relative">
                                        {/* Close Filter Button */}
                                        <button
                                            onClick={() => setShowFilter(false)}
                                            className="absolute top-3 right-3 text-[#0F2A4F] hover:text-blue-700"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>

                                        <div className="space-y-3">
                                            <div className="flex items-center">
                                                {/* Filter Selects/Inputs - Only Industry */}
                                                <div className="flex w-full gap-5">
                                                    {/* Industry Label Select */}
                                                    <div className="relative w-[30%]">
                                                        <select
                                                            className="w-full border border-gray-200 rounded-md px-3 py-2 bg-gray-50 text-[#0F2A4F] appearance-none pr-8"
                                                            defaultValue="Industry"
                                                        >
                                                            <option>Industry</option>
                                                        </select>
                                                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                            <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                    {/* Is Select */}
                                                    <div className="relative w-[30%]">
                                                        <select
                                                            className="w-full border border-gray-200 rounded-md px-3 py-2 bg-gray-50 text-[#0F2A4F] appearance-none pr-8"
                                                            defaultValue="is"
                                                        >
                                                            <option>Is</option>
                                                        </select>
                                                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                            <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                    {/* Industry Filter Select */}
                                                    <div className="relative w-[30%]">
                                                        <select
                                                            className="w-full border border-gray-200 rounded-md px-2 py-2 bg-gray-50 text-[#0F2A4F] appearance-none pr-8"
                                                            value={industryFilter}
                                                            onChange={(e) => setIndustryFilter(e.target.value)}
                                                        >
                                                            <option value="">All Industries</option>
                                                            {INDUSTRY_TYPES.map((industry) => (
                                                                <option key={industry} value={industry}>{industry}</option>
                                                            ))}
                                                        </select>
                                                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                            <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Customer Table with loading overlay */}
                                <div className="relative">
                                    {isTableLoading && (
                                        <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center h-full z-10">
                                            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0F2A4F]"></div>
                                        </div>
                                    )}
                                    <CustomerTable
                                        customers={showMyCustomers ? paginatedMyCustomers : paginatedAllCustomers}
                                        showOwnerColumn={!showMyCustomers}
                                        enableRowClick={true}
                                        fromDashboard={(customerId) => {
                                            // Check if this customer is in myCustomers list
                                            const isMyCustomer = myCustomers.some(c => c.customer_id === customerId);
                                            // If it's my customer, we want to go to edit mode (fromDashboard=false)
                                            // If it's not my customer, we stay on dashboard (fromDashboard=true)
                                            return !isMyCustomer;
                                        }}
                                    />
                                </div>

                                {/* Pagination Controls - Only show when not in "My Customers" mode */}
                                {!showMyCustomers && (
                                    <div className="flex justify-between items-center mt-4">
                                        <div className="text-sm text-gray-500">
                                            Showing {allCustomersRangeStart}-{allCustomersRangeEnd} of {filteredAllCustomers.length} customers
                                            {(debouncedSearchTerm || industryFilter) && (
                                                <span className="text-gray-400"> (filtered)</span>
                                            )}
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={handlePrevPage}
                                                disabled={currentPage === 1}
                                                className={`flex items-center px-3 py-1 border rounded ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-[#0F2A4F] hover:bg-gray-50'}`}
                                            >
                                                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                </svg>
                                                {/* Previous */}
                                            </button>

                                            {/* Page Numbers */}
                                            <div className="flex space-x-1">
                                                {getPaginationItems(currentPage, totalPages).map((item) => {
                                                    if (typeof item === 'string') {
                                                        return (
                                                            <span key={item} className="w-8 h-8 flex items-center justify-center text-gray-500">...</span>
                                                        );
                                                    }

                                                    return (
                                                        <button
                                                            key={item}
                                                            onClick={() => handlePageSelect(item)}
                                                            className={`w-8 h-8 flex items-center justify-center border rounded ${currentPage === item ? 'bg-[#0F2A4F] text-white' : 'text-[#0F2A4F]'}`}
                                                        >
                                                            {item}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <button
                                                onClick={handleNextPage}
                                                disabled={currentPage >= totalPages}
                                                className={`flex items-center px-3 py-1 border rounded ${currentPage >= totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-[#0F2A4F] hover:bg-gray-50'}`}
                                            >
                                                {/* Next */}
                                                <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Pagination Controls - My Customers mode */}
                                {showMyCustomers && (
                                    <div className="flex justify-between items-center mt-4">
                                        <div className="text-sm text-gray-500">
                                            Showing {filteredMyCustomers.length === 0 ? 0 : ((myCustomersCurrentPage - 1) * pageSize) + 1}-
                                            {Math.min(myCustomersCurrentPage * pageSize, filteredMyCustomers.length)} of {filteredMyCustomers.length} customers
                                            {(debouncedSearchTerm || industryFilter) && (
                                                <span className="text-gray-400"> (filtered)</span>
                                            )}
                                        </div>
                                        {myCustomersTotalPages > 1 && (
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => setMyCustomersCurrentPage(p => Math.max(1, p - 1))}
                                                    disabled={myCustomersCurrentPage === 1}
                                                    className={`flex items-center px-3 py-1 border rounded ${myCustomersCurrentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-[#0F2A4F] hover:bg-gray-50'}`}
                                                >
                                                    <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                    </svg>
                                                </button>
                                                <div className="flex space-x-1">
                                                    {Array.from({ length: Math.min(myCustomersTotalPages, 10) }).map((_, index) => {
                                                        const pageNum = index + 1;
                                                        if (pageNum <= 3 || (myCustomersTotalPages > 6 && pageNum > myCustomersTotalPages - 3) || myCustomersTotalPages <= 6) {
                                                            return (
                                                                <button
                                                                    key={pageNum}
                                                                    onClick={() => setMyCustomersCurrentPage(pageNum)}
                                                                    className={`w-8 h-8 flex items-center justify-center border rounded ${myCustomersCurrentPage === pageNum ? 'bg-[#0F2A4F] text-white' : 'text-[#0F2A4F]'}`}
                                                                >
                                                                    {pageNum}
                                                                </button>
                                                            );
                                                        }
                                                        if (pageNum === 4 && myCustomersTotalPages > 6) {
                                                            return <span key="ellipsis1" className="w-8 h-8 flex items-center justify-center text-gray-500">...</span>;
                                                        }
                                                        return null;
                                                    })}
                                                </div>
                                                <button
                                                    onClick={() => setMyCustomersCurrentPage(p => Math.min(myCustomersTotalPages, p + 1))}
                                                    disabled={myCustomersCurrentPage === myCustomersTotalPages}
                                                    className={`flex items-center px-3 py-1 border rounded ${myCustomersCurrentPage === myCustomersTotalPages ? 'text-gray-400 cursor-not-allowed' : 'text-[#0F2A4F] hover:bg-gray-50'}`}
                                                >
                                                    <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                }
            </div>

            {/* Add Customer Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-50">
                    <div className="bg-white h-full w-full sm:w-[500px] p-6 overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-[#0F2A4F] italic">Add New Customer</h2>
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="text-gray-400 hover:text-gray-600 rounded-full bg-gray-100 p-1"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <input
                                    type="text"
                                    placeholder="Customer Name"
                                    className={`w-full px-4 py-3 border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                                    value={newCustomer.name}
                                    onChange={(e) => {
                                        setNewCustomer({ ...newCustomer, name: e.target.value });
                                        if (formErrors.name) {
                                            setFormErrors(prev => ({ ...prev, name: '' }));
                                        }
                                    }}
                                />
                                {formErrors.name && (
                                    <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
                                )}
                            </div>

                            <div className="relative">
                                <select
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md appearance-none bg-white pr-10"
                                    value={newCustomer.industry}
                                    onChange={(e) => setNewCustomer({ ...newCustomer, industry: e.target.value })}
                                >
                                    {INDUSTRY_TYPES.map((industry) => (
                                        <option key={industry} value={industry}>{industry}</option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>

                            {/* Custom Industry Input Field */}
                            {newCustomer.industry === 'Custom' && (
                                <div>
                                    <input
                                        type="text"
                                        placeholder="Enter custom industry"
                                        className={`w-full px-4 py-3 border ${formErrors.customIndustry ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                                        value={newCustomer.customIndustry}
                                        onChange={(e) => setNewCustomer({ ...newCustomer, customIndustry: e.target.value })}
                                    />
                                    {formErrors.customIndustry && (
                                        <p className="mt-1 text-sm text-red-500">{formErrors.customIndustry}</p>
                                    )}
                                </div>
                            )}

                            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                                <p className="text-sm text-blue-800">
                                    <strong>Note:</strong> To get a head start choose an Industry from the dropdown. 
                                    The goals, trends and priorities will be seeded from a curated list. 
                                    You are then able to edit them as you see fit.
                                </p>
                                <p className="text-sm text-blue-800 mt-2">
                                    If you don't see an Industry match, or you don't want to use a pre-seeded Industry, 
                                    choose 'Custom' from the Industry list, enter your Industry in the Industry field, 
                                    and you can later create your own Goals, Priorities and Trends.
                                </p>
                            </div>

                            <div>
                                <div className="flex items-center justify-end gap-3">
                                    <span className="whitespace-nowrap text-sm font-medium text-[#0F2A4F]">
                                        ERP Account Number
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="ERP Account Number (optional)"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-right"
                                        value={newCustomer.ERPAccountNumber}
                                        onChange={(e) => setNewCustomer({ ...newCustomer, ERPAccountNumber: e.target.value })}
                                    />
                                    <HelpButton onClick={() => setShowErpInfo(true)} className="shrink-0" />
                                </div>
                            </div>

                            <div>
                                <input
                                    type="text"
                                    placeholder="Customer Owner"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md"
                                    value={currentUser?.name || newCustomer.createdBy}
                                    onChange={(e) => setNewCustomer({ ...newCustomer, createdBy: e.target.value })}
                                />
                            </div>

                            <button
                                onClick={handleAddCustomer}
                                disabled={isSubmitting}
                                className={`w-full bg-[#002D72] text-white italic font-medium py-3 px-4 rounded-md ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#001d4d]'} transition-colors flex items-center justify-center min-h-[48px]`}
                            >
                                {isSubmitting ? (
                                    <div className="flex items-center justify-center space-x-2">
                                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                        <span>Creating...</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center space-x-2">
                                        <span>Create</span>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <InfoModal
                isOpen={showErpInfo}
                onClose={() => setShowErpInfo(false)}
                title="ERP Account Number"
                message="ERP Account Number is used to ensure we can pull information about this account from other Presidio systems"
            />
        </div>
    );
};

export default Dashboard;
